import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { generateTagHtml, getEan8, printTags } from '../utils/printTag'

const TAG_PRINT_INFO_MAX = 24

function normalizeText(value) {
  return String(value || '').trim()
}

function normalizeWeight(value) {
  const raw = String(value || '').trim().replace(',', '.')
  if (!raw) return ''
  const parsed = Number.parseFloat(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) return ''
  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(2).replace(/\.?0+$/, '')
}

function buildDraft(product) {
  const source = product || {}
  const metafields = source?.metafields || {}
  const tagPrintInfo = normalizeText(source?.tagPrintInfo || metafields?.tag_print_info).slice(0, TAG_PRINT_INFO_MAX)

  return {
    title: normalizeText(source?.title),
    productType: normalizeText(source?.productType),
    weight: normalizeWeight(source?.weight || metafields?.weight),
    karat: normalizeText(source?.karat || metafields?.karat),
    tagPrintInfo,
    price: String(source?.price ?? '0'),
    sku: normalizeText(source?.sku),
    barcode: String(source?.barcode || '').replace(/\D/g, '').slice(0, 8),
    metafields: { ...(metafields || {}) }
  }
}

export default function PostPublishTagPreviewModal({
  open,
  product,
  tagConfig,
  onClose
}) {
  const [draft, setDraft] = useState(() => buildDraft(product))

  useEffect(() => {
    if (!open) return
    setDraft(buildDraft(product))
  }, [open, product])

  const close = useCallback(() => {
    onClose?.()
  }, [onClose])

  useEffect(() => {
    if (!open) return
    const handleEscape = (event) => {
      if (event.key === 'Escape') close()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [open, close])

  const lockedBarcode = useMemo(() => {
    const resolved = getEan8({
      ...draft,
      metafields: {
        ...(draft?.metafields || {}),
        karat: draft?.karat || '',
        weight: draft?.weight || '',
        tag_print_info: draft?.tagPrintInfo || ''
      }
    })
    // Verbatim: the label barcode now encodes the SKU as-is (CODE128).
    return String(resolved || draft?.barcode || '')
  }, [draft])

  const previewProduct = useMemo(() => ({
    title: draft.title,
    price: draft.price,
    sku: draft.sku,
    barcode: lockedBarcode,
    weight: draft.weight,
    karat: draft.karat,
    productType: draft.productType,
    tagPrintInfo: draft.tagPrintInfo,
    metafields: {
      ...(draft.metafields || {}),
      karat: draft.karat || '',
      weight: draft.weight || '',
      tag_print_info: draft.tagPrintInfo || ''
    }
  }), [draft, lockedBarcode])

  const previewHtml = useMemo(() => (
    generateTagHtml([previewProduct], tagConfig, { preview: true, previewScale: 2 })
  ), [previewProduct, tagConfig])

  const updateDraft = useCallback((key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handlePrint = useCallback(() => {
    printTags([previewProduct], tagConfig, {
      windowWidth: 800,
      windowHeight: 900
    })
  }, [previewProduct, tagConfig])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />

      <div
        className="relative w-[min(96vw,1200px)] max-h-[92vh] overflow-hidden rounded-2xl border border-border/60 bg-popover shadow-elevation-4 flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground truncate">Tag Preview Before Printing</h3>
            <p className="text-xs text-muted-foreground">
              Product listed in POS. Edit only Tag Print Info before printing.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(520px,1fr)] gap-4">
            <section className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
              <h4 className="text-sm font-semibold text-foreground">Editable Label Info</h4>
              <p className="text-xs text-muted-foreground">
                Only <strong>Tag Print Info</strong> is editable here. This is the main text printed on the label.
              </p>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs text-muted-foreground">Tag Print Info</label>
                  <span className="text-[11px] text-muted-foreground">
                    {(draft.tagPrintInfo || '').length}/{TAG_PRINT_INFO_MAX}
                  </span>
                </div>
                <input
                  type="text"
                  value={draft.tagPrintInfo}
                  maxLength={TAG_PRINT_INFO_MAX}
                  onChange={(event) => updateDraft('tagPrintInfo', event.target.value.slice(0, TAG_PRINT_INFO_MAX).toUpperCase())}
                  className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div className="pt-2 border-t border-border/60">
                <h5 className="text-xs font-semibold text-foreground mb-2">Locked Fields (Read-only)</h5>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Price</label>
                    <input
                      type="text"
                      value={`$${Number.parseFloat(draft.price || '0').toFixed(2)}`}
                      readOnly
                      className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">SKU</label>
                    <input
                      type="text"
                      value={draft.sku || '-'}
                      readOnly
                      className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm font-mono cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Barcode (scans as SKU)</label>
                    <input
                      type="text"
                      value={lockedBarcode || '-'}
                      readOnly
                      className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm font-mono cursor-not-allowed"
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-border/60 bg-card p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-foreground">Amplified Preview (2x)</h4>
                <span className="text-xs text-muted-foreground">2.25" x 0.5" label</span>
              </div>
              <div className="rounded-lg border border-border/60 bg-muted/20 overflow-hidden">
                <iframe
                  title="Post publish label preview"
                  srcDoc={previewHtml}
                  className="w-full h-[250px] md:h-[290px] bg-transparent"
                />
              </div>
            </section>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border/60 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={close}>
            Close
          </Button>
          <Button variant="default" onClick={handlePrint}>
            <Icon name="Printer" size={14} className="mr-1" />
            Print Tag
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
